import { useState, useCallback, useMemo } from 'react';

export type SkeletonMetrics = {
    startTime: number | null;
    duration: number | null;
};

export type SkeletonMetricsResult = {
    trackSkeletonStart: () => void;
    trackSkeletonEnd: () => void;
    metrics: SkeletonMetrics;
    isTracking: boolean;
};

export type SkeletonMetricsConfig = {
    componentName: string;
};

/**
 * Hook pour tracker les performances de chargement des skeletons
 * Fournit des métriques de temps et des logs de debug pour optimiser l'UX
 *
 * @param params - Configuration du tracking
 * @param params.componentName - Nom du composant pour identifier les logs
 * @returns Objet avec les contrôles de tracking et les métriques
 *
 * @example
 * ```tsx
 * const { trackSkeletonStart, trackSkeletonEnd, metrics, isTracking } = useSkeletonMetrics({
 *   componentName: 'UserProfileCard'
 * });
 *
 * useEffect(() => {
 *   trackSkeletonStart();
 *
 *   fetchUserData().then(data => {
 *     // Process data
 *     trackSkeletonEnd();
 *   });
 * }, []);
 *
 * return (
 *   <div>
 *     {isTracking ? <UserSkeleton /> : <UserProfile />}
 *     {metrics.duration && (
 *       <div>Loaded in {metrics.duration}ms</div>
 *     )}
 *   </div>
 * );
 * ```
 */
export const useSkeletonMetrics = ({
    componentName,
}: SkeletonMetricsConfig): SkeletonMetricsResult => {
    const [metrics, setMetrics] = useState<SkeletonMetrics>({
        startTime: null,
        duration: null,
    });
    const [isTracking, setIsTracking] = useState(false);

    const trackSkeletonStart = useCallback(() => {
        const startTime = Date.now();
        setMetrics((prev) => ({ ...prev, startTime }));
        setIsTracking(true);
        console.debug(`🔄 [SkeletonMetrics] ${componentName} - Loading started`);
    }, [componentName]);

    const trackSkeletonEnd = useCallback(() => {
        if (!isTracking || metrics.startTime === null) return;

        const endTime = Date.now();
        const duration = endTime - metrics.startTime;

        setMetrics((prev) => ({ ...prev, duration }));
        setIsTracking(false);

        console.debug(`✅ [SkeletonMetrics] ${componentName} - Loading completed in ${duration}ms`);

        if (duration > 2000) {
            console.warn(`⚠️ [SkeletonMetrics] ${componentName} took ${duration}ms to load (>2s)`);
        }
    }, [componentName, isTracking, metrics.startTime]);

    const result = useMemo(
        () => ({
            trackSkeletonStart,
            trackSkeletonEnd,
            metrics,
            isTracking,
        }),
        [trackSkeletonStart, trackSkeletonEnd, metrics, isTracking],
    );

    return result;
};
